import * as Sentry from '@sentry/react';
import { Col, Row } from "antd";
import SpinLoader from "components/Loader/SpinLoader";
import SentryWrap from "components/Sentry";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePlacementExamSession } from "store/placement_exam/selectors";
import {
  handleClearPlacementSession,
  handlePlacementExamAiGrading,
  handleUpdatePlacementExamSubmissions,
} from "store/placement_exam/thunks";
import { goFullScreen } from "utils";
import { placement_exam_logo } from "utils/assets/images";
import PlacementAssignment from "./PlacementAssignment";
import { FullScreenModal } from "./SectionVideoPlayer";
import useTouchDetection from 'components/hooks/useTouchDeviceDetection';

const useTimer = (durationInMinutes, onFinishCallback) => {
  const [timeLeft, setTimeLeft] = useState(() => {
    // Retrieve remaining time from sessionStorage if it exists
    const storedTime = sessionStorage.getItem("remainingTime");
    return storedTime ? parseInt(storedTime, 10) : durationInMinutes * 60; // Convert minutes to seconds
  });
  const timerRef = useRef(null);

  useEffect(() => {
    if (timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            onFinishCallback();
            return 0;
          }
          const newTime = prev - 1;
          sessionStorage.setItem("remainingTime", newTime); // Store remaining time in sessionStorage
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current); // Cleanup timer on unmount
    };
  }, [timeLeft, onFinishCallback]);

  return moment.utc(timeLeft * 1000).format("mm:ss");
};

const PlacementExamQuestion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showAlert = useAlert();
  const sessionInfo = usePlacementExamSession();
  const [loader, setLoader] = useState(false);
  const distConfig = sessionInfo.exam_info?.config || {};

  const { session_info, exam_info } = sessionInfo;
  const { assignment } = exam_info || {};
  const { questions, duration } = assignment || {};
  const [openFullScreen, setOpenFullScreen] = useState(false);
  const isTouchDevice =  useTouchDetection();

  // Memoize handleFinalSubmission to prevent recreation on every render
  const handleFinalSubmission = useCallback(async () => {
    setLoader(true);
    try {
      if (sessionInfo.submission?.id) {
        showAlert("Updating...", "info");
        const result = await dispatch(
          handleUpdatePlacementExamSubmissions({
            // answers: sessionInfo.answers || [],
            completed: true,
            id: sessionInfo.submission.id,
          })
        );

        if (result?.success) {
          await handlePlacementExamAiGrading(sessionInfo.submission.id);
          setTimeout(() => dispatch(handleClearPlacementSession()), 1000);
          sessionStorage.removeItem("remainingTime"); // Clear remaining time on final submission
          sessionStorage.removeItem("switch_tab_count"); // Clean up tab switch tracking
          sessionStorage.removeItem("fullscreen_refresh_count"); // Clean up fullscreen refresh tracking
          navigate("/placement-exam/final-submission?submission_state=complete_with_time");
        } else {
          console.log(result?.message, "result?.message");
          showAlert(result?.message || "Something went wrong", 'error');
        }
      } else {
        showAlert("Submission not found", 'error');
      }
    } catch (error) {
      console.log(error.message, "error?.message");
      Sentry.captureException(`Error in handleFinalSubmission: ${error}`);
      showAlert(error.message || "Something went wrong", 'error');
    } finally {
      setLoader(false);
    }
  }, [dispatch, navigate, sessionInfo, showAlert]);

  // Main security and event handling useEffect
// Main security and event handling useEffect
useEffect(() => {
  
  function isInputFocused() {
    const activeElement = document.activeElement;
      const inputType = ['text','search','email','number','password','INPUT','TEXTAREA','sun-editor-editable']
      return inputType.includes(activeElement.type) || isTouchDevice;
  }
  if (!isTouchDevice) {
    if (!isInputFocused()) {
      const fullscreenRefreshCount = parseInt(sessionStorage.getItem("fullscreen_refresh_count") || "0", 10);
      if (fullscreenRefreshCount >= 2 && !document.fullscreenElement) {
        showAlert("You have exited fullscreen multiple times. Submitting exam automatically.", "error");
        sessionStorage.removeItem("fullscreen_refresh_count");
        setTimeout(() => {
          handleFinalSubmission();
        }, 1000);
        return;
      }
      if (fullscreenRefreshCount > 0 && fullscreenRefreshCount < 2 && !document.fullscreenElement) {
        showAlert(`Please stay in fullscreen mode.it will be submitted automatically if you exit fullscreen again`);
        setOpenFullScreen(true);
      }
    }
  }

  // Handle tab switching and visibility changes
  async function handleVisibilityChange() {
    if (document.hidden) {
      const switchTabCount = sessionStorage.getItem("switch_tab_count") || "0";
      if (switchTabCount === "1") {
        handleFinalSubmission();
        sessionStorage.removeItem("switch_tab_count");
        return;
      }
      alert("Please do not switch tabs or navigate away, as it will be automatically submitted next time.");
      sessionStorage.setItem("switch_tab_count", "1");
    }
  }

  // Handle page reload/close attempts
  function handleBeforeUnload(event) {
     event.preventDefault();
      event.returnValue = "Please do not refresh the page, as it will be automatically submitted next time.";

      const switchTabCount = sessionStorage.getItem("switch_tab_count") || "0";
      if (switchTabCount === "1") {
        handleFinalSubmission();
        sessionStorage.removeItem("switch_tab_count");
      } else {
        sessionStorage.setItem("switch_tab_count", "1");
      }
      if (!isInputFocused() && !document.fullscreenElement && !isTouchDevice) {
        const fullscreenRefreshCount = parseInt(sessionStorage.getItem("fullscreen_refresh_count") || "0", 10);
        sessionStorage.setItem("fullscreen_refresh_count", (fullscreenRefreshCount + 1).toString());
      }
  }

  // Prevent exiting fullscreen
  function handleFullScreenChange() {
     if (isInputFocused()) {
      return;
    }
    if (!document.fullscreenElement) {
      setOpenFullScreen(true);
    }
  }


  function handleKeyDown(event) {
    // Check if an input or textarea is focused
    if (isInputFocused()) {
      return; // Ignore events if focused on input or textarea
    }

    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showAlert("Exiting fullscreen is not allowed during the exam", "warning");
      
      // Force re-enter fullscreen if somehow exited
      if (!document.fullscreenElement) {
        setTimeout(() => goFullScreen(), 50);
      }
      return false;
    }

    // Prevent F11 key (toggles fullscreen) - PRIORITY BLOCK
    if (event.key === 'F11' || event.keyCode === 122) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showAlert("Toggling fullscreen is not allowed during the exam", "warning");
      
      // Ensure we stay in fullscreen
      if (!document.fullscreenElement) {
        setTimeout(() => goFullScreen(), 50);
      }
      return false;
    }

    // Prevent Alt+Tab (switch applications - can exit fullscreen)
    if (event.altKey && event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showAlert("Switching applications is not allowed during the exam", "warning");
      return false;
    }

    // Prevent Alt+Shift+Tab (reverse switch applications)
    if (event.altKey && event.shiftKey && event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showAlert("Switching applications is not allowed during the exam", "warning");
      return false;
    }

    // Prevent Windows key (opens start menu - can exit fullscreen)
    if (event.key === 'Meta' || event.key === 'OS' || event.keyCode === 91 || event.keyCode === 92) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showAlert("Opening system menu is not allowed during the exam", "warning");
      return false;
    }

    // Prevent Windows+D (show desktop)
    if (event.metaKey && (event.key === 'd' || event.key === 'D')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showAlert("Showing desktop is not allowed during the exam", "warning");
      return false;
    }

    // Prevent F5 key (refresh)
    if (event.key === 'F5' || event.keyCode === 116) {
      event.preventDefault();
      event.stopPropagation();
      showAlert("Refresh is disabled during the exam", "warning");
      return false;
    }
    
    // Prevent Ctrl+R (refresh)
    if (event.ctrlKey && (event.key === 'r' || event.key === 'R')) {
      event.preventDefault();
      event.stopPropagation();
      showAlert("Refresh is disabled during the exam", "warning");
      return false;
    }

    // Add all your other existing key blocks here...
  }

  // Prevent right-click context menu
  function handleContextMenu(event) {
    if (isInputFocused()) {
      return;
    }
    event.preventDefault();
    showAlert("Right-click is disabled during the exam", "warning");
  }

  // Initialize fullscreen after component mount
  const fullscreenTimer = setTimeout(() => {
    if(isTouchDevice) return;
    goFullScreen();
  }, 500);

  // Add all event listeners
  document.addEventListener("visibilitychange", handleVisibilityChange);
  document.addEventListener("fullscreenchange", handleFullScreenChange);
  document.addEventListener("keydown", handleKeyDown);
  document.addEventListener("contextmenu", handleContextMenu);
  window.addEventListener("beforeunload", handleBeforeUnload);

  // Cleanup function
  return () => {
    clearTimeout(fullscreenTimer);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("fullscreenchange", handleFullScreenChange);
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("contextmenu", handleContextMenu);
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, [isTouchDevice, handleFinalSubmission, showAlert]);

  const formattedTime = useTimer(duration, handleFinalSubmission);

  // Navigation and setup useEffect
  useEffect(() => {
    if (!session_info?.id) {
      navigate("/placement-exam");
    } else if (exam_info?.config?.logo) {
      sessionStorage.setItem("exam_dist_logo", exam_info.config.logo);
    }
  }, [session_info, exam_info, navigate]);

  // Memoized header content for performance
  const headerContent = useMemo(
    () => (
      <header className="studentFormHeader studntQuestionHedr">
        <Row align="middle">
          <Col xs={4} sm={4} md={4} className="logo-col">
            <div className="StudentSecondLogoPart mnageEductionLogo">
              <img src={placement_exam_logo} alt="Placement Exam Logo" />
            </div>
          </Col>
          <Col xs={16} sm={16} md={16} className="logo-col">
            <div className="studentFormLogo mngeCoronaLogo">
              {distConfig.logo && <img src={distConfig.logo} alt="Placement District Logo" />}
            </div>
          </Col>
          <Col xs={4} sm={4} md={4} className="hedrTimer">
            {formattedTime && duration ? (
              <div className="stdntQstntimer">
                <span>{formattedTime}</span>
              </div>
            ) : null}
          </Col>
        </Row>
      </header>
    ),
    [distConfig.logo, formattedTime, duration]
  );

  return (
    <SentryWrap>
      <SpinLoader dataloader={loader} />
      <FullScreenModal 
        show={openFullScreen} 
        onOk={() => setOpenFullScreen(false)}
        isTabAndMobile={isTouchDevice}
      />
      <div className="exam-container">
        {headerContent}
        <PlacementAssignment 
          sections={questions || []} 
          session_id={session_info?.id || 0} 
        />
      </div>
    </SentryWrap>
  );
};

export default PlacementExamQuestion;